package com.essntl.uikit.components.input.money

import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.*
import androidx.compose.ui.unit.dp
import com.essntl.core.utils.utils.formatMoney
import com.essntl.uikit.components.input.EsInput
import core.validation.EsInputState
import kotlin.math.max

fun getEsMoneyInputVisualTransformation(currency: String) = VisualTransformation {
    val transformedText =
        if (it.text.isBlank()) {
            ""
        } else {
            "$currency${formatMoney(it.text)}"
        }

    val commaIndices =
        transformedText.mapIndexedNotNull { index, c ->
            if (c == ',') {
                index
            } else {
                null
            }
        }

    TransformedText(
        text =
        AnnotatedString(
            text = transformedText,
        ),
        offsetMapping =
        object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int =
                if (it.isBlank()) {
                    offset
                } else {
                    val commaCount =
                        commaIndices.count { i ->
                            i <= offset
                        }

                    offset + currency.length + commaCount
                }

            override fun transformedToOriginal(offset: Int): Int =
                if (it.isBlank()) {
                    offset
                } else {
                    val commaCount =
                        commaIndices.count { i ->
                            i < offset
                        }

                    max(0, offset - currency.length - commaCount)
                }
        },
    )
}

@Composable
fun EsMoneyInput(
    value: Double,
    onValueChange: (Double) -> Unit,
    currency: String = "$",
    label: String? = null,
    onClear: () -> Unit = {},
    infos: @Composable (() -> Unit)? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default.copy(
            keyboardType = KeyboardType.Decimal,
            autoCorrectEnabled = false,
        ),
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    modifier: Modifier = Modifier,
) {
    var selection by remember {
        mutableStateOf(TextRange.Zero)
    }
    var textFieldValue by remember {
        mutableStateOf(TextFieldValue())
    }
    var isEndWithDot by remember {
        mutableStateOf(false)
    }
    var isWithDot by remember {
        mutableStateOf(false)
    }

    LaunchedEffect(value, currency, selection, isEndWithDot) {
        if (value == 0.0) {
            textFieldValue = TextFieldValue()
        } else if (value > 0.0) {
            val dot = if (isEndWithDot) "." else ""
            val stringValue =
                value.toString().let {
                    if (isWithDot) {
                        it
                    } else {
                        it.removeSuffix(".0")
                    }
                }
            val newText = "$stringValue$dot"

            textFieldValue =
                TextFieldValue(
                    text = newText,
                    selection = selection,
                )
        }
    }

    EsInput(
        value = textFieldValue,
        onClear = onClear,
        onValueChange = {
            val stringInt =
                it.text
                    .replace(currency, "")
                    .trim()
                    .also { s ->
                        isEndWithDot = s.endsWith(".") && s.count { c -> c == '.' } == 1
                    }
                    .removeSuffix(".")

            isWithDot = stringInt.contains(".")

            val newValue =
                if (stringInt.isEmpty()) {
                    0.0
                } else {
                    stringInt.toDoubleOrNull() ?: return@EsInput
                }

            selection = it.selection

            val decimalPart =
                if (isWithDot) {
                    stringInt.split('.')[1]
                } else {
                    ""
                }

            if (
                newValue != value &&
                newValue.toString().length < 15 &&
                decimalPart.length <= 2
            ) {
                onValueChange(newValue)
            }
        },
        label = label,
        infos = infos,
        placeholder = "${currency}0.0",
        message = message,
        prefix = prefix,
        suffix = suffix,
        showClearButton = false,
        enabled = enabled,
        readOnly = readOnly,
        state = state,
        visualTransformation = getEsMoneyInputVisualTransformation(currency),
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        singleLine = singleLine,
        maxLines = maxLines,
        minLines = minLines,
        interactionSource = interactionSource,
        cursorColor = cursorColor,
        shape = shape,
        modifier = modifier,
    )
}

@Composable
fun EsMoneyInput(
    value: Int,
    onValueChange: (Int) -> Unit,
    currency: String = "$",
    label: String? = null,
    onClear: () -> Unit = {},
    infos: @Composable (() -> Unit)? = null,
    message: String? = null,
    prefix: @Composable (() -> Unit)? = null,
    suffix: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = !enabled,
    state: EsInputState = EsInputState.Default,
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default.copy(
            keyboardType = KeyboardType.Decimal,
            autoCorrectEnabled = false,
        ),
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    singleLine: Boolean = false,
    maxLines: Int = if (singleLine) 1 else Int.MAX_VALUE,
    minLines: Int = 1,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    cursorColor: Color = Color.Unspecified,
    shape: Shape = RoundedCornerShape(12.dp),
    modifier: Modifier = Modifier,
) {
    var selection by remember {
        mutableStateOf(TextRange.Zero)
    }
    var textFieldValue by remember {
        mutableStateOf(TextFieldValue())
    }

    LaunchedEffect(value, currency, selection) {
        textFieldValue =
            if (value == 0) {
                TextFieldValue()
            } else {
                TextFieldValue(
                    text = value.toString(),
                    selection = selection,
                )
            }
    }

    EsInput(
        value = textFieldValue,
        onClear = onClear,
        onValueChange = {
            val stringInt =
                it.text
                    .replace(currency, "")
                    .trim()

            val newValue =
                if (stringInt.isEmpty()) {
                    0
                } else {
                    stringInt.toIntOrNull() ?: return@EsInput
                }

            selection = it.selection

            if (newValue != value && newValue.toString().length < 8) {
                onValueChange(newValue)
            }
        },
        label = label,
        infos = infos,
        placeholder = "0 $currency",
        message = message,
        prefix = prefix,
        suffix = suffix,
        showClearButton = false,
        enabled = enabled,
        readOnly = readOnly,
        state = state,
        visualTransformation = getEsMoneyInputVisualTransformation(currency),
        keyboardOptions = keyboardOptions,
        keyboardActions = keyboardActions,
        singleLine = singleLine,
        maxLines = maxLines,
        minLines = minLines,
        interactionSource = interactionSource,
        cursorColor = cursorColor,
        shape = shape,
        modifier = modifier,
    )
}
